import { useQuery } from "@tanstack/react-query";

import type {
	ShipmentCheckCall,
	ShipmentException,
	ShipmentManagement,
	ShipmentRemark,
} from "~/shared/schemas/shipment-management-schema.ts";
import { shipmentManagementSchema } from "~/shared/schemas/shipment-management-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";

import { groupRemarksOrCheckCallsByDate } from "../utils/group-remarks-by-date.ts";

const fetchShipment = async (
	shipmentPo: number,
	isInternal: boolean,
	signal: AbortSignal,
) => {
	const baseUrl = isInternal ? apiRoutes.SHIPMENT : apiRoutes.CUSTOMERSHIPMENT;

	const url = baseUrl.replace(":poNumber", shipmentPo.toString());

	const resp = await getAndValidate(url, shipmentManagementSchema, { signal });

	if (resp.success) {
		// formatter
		const immediateActionExceptions = resp.data.exceptions?.filter(
			(exception) => exception.isImmediateAction,
		);
		const needsAcknowledgedExceptions = resp.data.exceptions?.filter(
			(exception) => exception.isNeedsAcknowledged,
		);
		const groupedRemarks = groupRemarksOrCheckCallsByDate(
			resp.data.remarks ?? [],
		);
		const groupedCheckCalls = groupRemarksOrCheckCallsByDate(
			resp.data.trackingHistory.checkCalls ?? [],
		);
		return {
			...resp.data,
			immediateActionExceptions,
			needsAcknowledgedExceptions,
			groupedRemarks,
			groupedCheckCalls,
		};
	}

	throw new Error(resp.error);
};

export type AugmentedShipment = ShipmentManagement & {
	immediateActionExceptions: ShipmentException[] | undefined;
	needsAcknowledgedExceptions: ShipmentException[] | undefined;
	groupedRemarks: Record<string, ShipmentRemark[]>;
	groupedCheckCalls: Record<string, ShipmentCheckCall[]>;
};

export const useGetShipment = (shipmentPo: number, isInternal: boolean) => {
	const hasValidParams = Number.isFinite(shipmentPo) && shipmentPo > 0;

	const { data, error, isLoading, isFetching } = useQuery({
		queryKey: ["shipment", shipmentPo, isInternal],
		queryFn: ({ signal }) => fetchShipment(shipmentPo, isInternal, signal),
		enabled: hasValidParams,
		staleTime: 5 * 60 * 1000, // 5 minutes
		retry: false,
		// An errored query has no data, so refetchOnMount is never consulted;
		// retryOnMount must stay true or a failed PO re-shows the cached error
		// on every revisit without re-requesting it.
		retryOnMount: true,
		refetchOnWindowFocus: false,
		refetchOnMount: "always",
	});

	const invalidParamsError = hasValidParams
		? null
		: new Error(
				`Invalid shipment identifiers. PO Number "${shipmentPo}" must be a valid number greater than zero.`,
			);

	return {
		shipment: data,
		error: invalidParamsError ?? error,
		isLoading: hasValidParams && isLoading,
		isFetching: hasValidParams && isFetching,
	};
};
